import React from 'react';

import { ClaimsFormStatus, ProfileSection } from 'Containers/ProfilePage/types';
import { ProgressSection, useProfileContext, CtaNotification } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import { FadeIn, Spinner } from 'DesignLibrary/atoms';
import { Icon } from 'DesignLibrary/atoms/Icon';
import { Tooltip } from 'DesignLibrary/atoms/Tooltip';
import { useViewport } from 'DesignLibrary/context';
import { StackedIcon } from 'Shared/StackedIcon';

import { ProfileContainer } from './styled';

export interface ProfileProps {
  claimsFormStatus: ClaimsFormStatus;
  claimsSummaryPolling: boolean;
}

export const Profile = ({ claimsFormStatus, claimsSummaryPolling }: ProfileProps) => {
  const { profileProgressMap, getIsFormValid, getIsFormCompleted, markNotificationViewed } =
    useProfileContext();
  const { retrieveContentfulData } = useTextContext();
  const { device } = useViewport();

  // Check that profile questions are complete
  const isFormValid = getIsFormValid();
  const isFormUnlocked = getIsFormCompleted();
  const isFormComplete = isFormValid && isFormUnlocked;

  const getSectionIcon = (sectionKey: string, progressSection: ProgressSection): React.ReactNode | null => {
    const { isUnlocked, isValid, isDirty, ctaNotification } = progressSection;

    // Locked state
    if (!isUnlocked) {
      return (
        <Icon
          type="Circle"
          size="large"
          weight="fill"
          color="--colors-gray-300"
          key={`cta-step-${sectionKey}`}
        />
      );
    }

    // Claims loading state
    if (sectionKey === 'claims' && (claimsSummaryPolling || claimsFormStatus === ClaimsFormStatus.Loading)) {
      return (
        <StackedIcon
          circleArgs={{ color: '--colors-gray-300', size: 'large' }}
          dataTestId={`cta-loading-${sectionKey}`}
          stackedIcon={<Spinner size="small" />}
        />
      );
    }

    // Notification state
    if (ctaNotification === CtaNotification.Error) {
      return (
        <StackedIcon
          circleArgs={{ size: 'large', weight: 'fill', color: '--colors-alex-orange' }}
          dataTestId={`cta-warning-${sectionKey}`}
          stackedIcon={<Icon type="Warning" color="--colors-yellow-600" size="small" weight="fill" />}
        />
      );
    }
    if (ctaNotification === CtaNotification.Success) {
      return (
        <StackedIcon
          circleArgs={{ size: 'large', weight: 'fill', color: '--colors-blue-100' }}
          dataTestId={`cta-notification-${sectionKey}`}
          stackedIcon={<Icon type="BellRinging" color="--primary-blue" size="small" weight="fill" />}
        />
      );
    }

    // Error state
    if (isDirty && !isValid) {
      return <Icon type="XCircle" color="--primary-red" size="large" weight="fill" />;
    }

    // Complete, valid state
    if (isValid) {
      return <Icon type="CheckCircle" color="--primary-blue" size="large" weight="fill" />;
    }

    // Default, unlocked state
    return <Icon type="Circle" color="--colors-gray-300" size="large" weight="regular" />;
  };

  const renderProfileProgressIcons = () => {
    const sections = Object.entries(profileProgressMap) as [ProfileSection, ProgressSection][];

    return sections
      .map(([key, progressSection]) => {
        if (progressSection.isDisabled) {
          return null;
        }
        const icon = getSectionIcon(key, progressSection);

        const routeLabel = retrieveContentfulData<string>(`call_to_action.tooltips.profile.${key}`);

        return (
          <React.Fragment key={key}>
            {device !== 'mobile' ? (
              <a
                key={`cta-step-${key}`}
                href={`#${key}-section`}
                data-testid={`cta-step-${key}`}
                // TODO: Contentful
                aria-label={`Scroll to ${routeLabel} section`}
                onClick={() => markNotificationViewed(key)}
              >
                <Tooltip
                  isInteractive
                  id={`cta-${key}-tooltip`}
                  label={routeLabel}
                  desktopOverrides={{
                    offset: [0, 24],
                  }}
                  aria-hidden
                >
                  {icon}
                </Tooltip>
              </a>
            ) : (
              <a key={`cta-step-${key}`} href={`#${key}-section`} data-testid={`cta-step-${key}`}>
                {icon}
              </a>
            )}
          </React.Fragment>
        );
      })
      .filter((i) => i);
  };

  return (
    <div style={{ zIndex: 1 }}>
      <FadeIn isOpen={device !== 'mobile' || !isFormComplete}>
        <ProfileContainer>{renderProfileProgressIcons()}</ProfileContainer>
      </FadeIn>
    </div>
  );
};

export default Profile;
